import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { useHead, useSeoMeta } from "@unhead/react"
import {AlertCircle, ArrowLeft, Calendar, CheckCircle2, Clock, Droplets, MapPin, Send} from 'lucide-react'
import HavenNav from "@/components/HavenNav"

const JANE_BOOK_NOW = "https://havenintegrativehealth.janeapp.com/locations/haven-wellness-health-iv-therapy/book#/staff_member/1/treatment/10"

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
}

const serviceAreas = [
  "Vancouver",
  "Burnaby",
  "Coquitlam",
  "Gilmore Place",
  "New Westminster",
  "Richmond",
  "Surrey",
  "Langley",
  "Delta",
  "White Rock",
  "South Surrey",
  "North Vancouver",
  "West Vancouver",
]

const myths = [
  {
    number: "01",
    myth: "IV Nutrient Therapy Is Just a Trend With No Real Benefit",
    truth:
      "Intravenous vitamin and mineral delivery has been used in clinical settings for decades. IV nutrient therapy bypasses the digestive system, allowing vitamins, minerals, electrolytes, and amino acids to be absorbed directly into the bloodstream at close to 100% bioavailability. Oral supplements, by comparison, can lose a significant portion of their potency to digestion before ever reaching your cells. Mobile IV therapy simply brings this well-established method of hydration and vitamin therapy to your home, office, or event across Metro Vancouver, rather than requiring a hospital or clinic visit.",
  },
  {
    number: "02",
    myth: "IV Hydration Therapy Is Only for Hangovers",
    truth:
      "Hangover recovery is one of the most searched reasons people book mobile IV therapy, but it is far from the only one. Clients across Vancouver, Burnaby, and the Lower Mainland book IV drips for travel recovery and jet lag, pre- and post-workout athletic recovery, immune support during cold and flu season, stress and fatigue management, migraine relief, and everyday dehydration from busy schedules, heat, or illness. Corporate wellness events, bridal parties, and bachelorette bookings across Coquitlam, Langley, and Surrey are increasingly common as more people discover IV hydration as a proactive wellness tool, not just a recovery fix.",
  },
  {
    number: "03",
    myth: "Mobile IV Therapy Is Unsafe Without a Doctor Present",
    truth:
      "Reputable mobile IV therapy providers do not simply send a nurse to your door unsupervised. At Haven Wellness Health, every client completes a Health History form that is reviewed by a pharmacist and nurse practitioner before treatment is approved. Your infusion is then administered by a registered nurse trained in IV insertion and patient monitoring, who stays with you for the full session and monitors your vitals throughout. This layered clinical oversight, pharmacist review, NP approval, and RN administration, is standard for safe, regulated in-home IV therapy in Vancouver and surrounding areas like Delta, White Rock, and North Vancouver.",
  },
  {
    number: "04",
    myth: "All IV Drips Are Basically the Same",
    truth:
      "Not all IV nutrient blends are formulated the same way, and the right drip depends on your goals. A hydration-focused blend for post-workout recovery looks very different from a stress-support formula with added magnesium and taurine, or a skin-supportive blend built around Vitamin C, biotin, zinc, and selenium. Iron infusion therapy for confirmed iron deficiency is a distinct medical service entirely, using formulations like Monoferric or Venofer under close nursing supervision. When you book mobile IV therapy in Burnaby, Vancouver, or anywhere in the Lower Mainland, your consultation should match the drip to your specific symptoms and wellness goals, not offer a one-size-fits-all bag.",
  },
  {
    number: "05",
    myth: "IV Therapy Hurts and Takes All Day",
    truth:
      "Most in-home IV therapy sessions are completed within 30 to 60 minutes from the moment your nurse arrives to the end of your infusion, aside from longer iron infusions which are intentionally administered slowly over several hours for safety. As for discomfort, registered nurses use small, pediatric-sized IV catheters, so most clients feel no more than a quick pinch. Numbing cream is available on request for anyone sensitive to needles. The convenience of mobile IV hydration, delivered to your home, office, or hotel across Vancouver, Burnaby, Coquitlam, Richmond, or Surrey, means no waiting rooms and no travel time either.",
  },
  {
    number: "06",
    myth: "IV Vitamin Therapy Is Only for the Wealthy or a Luxury Indulgence",
    truth:
      "While IV nutrient therapy is often positioned as a premium wellness service, it is increasingly used as a practical health tool, not just an indulgence. Many extended health plans, Health Spending Accounts (HSA), and Flexible Spending Accounts (FSA) may reimburse part of the cost, and detailed invoices are available on request. Group and event pricing also makes mobile IV therapy accessible for corporate wellness days, bridal parties, and fitness studio bookings across the Lower Mainland, from Langley and Surrey to West Vancouver and Gilmore Place in Burnaby, spreading the value across multiple guests at once.",
  },
  {
    number: "07",
    myth: "Anyone Can Just Show Up and Get Any IV Drip",
    truth:
      "Responsible mobile IV therapy providers do not treat every client identically or skip clinical screening. Before any infusion, your complete health history is reviewed by a pharmacist and nurse practitioner to confirm you are a safe candidate for your selected treatment. Certain medical conditions, medications, or pregnancy status may require modifications or a conversation with your physician first. This is why Haven Wellness Health requires a Health History form and clinical review before every booking, whether you are in downtown Vancouver, North Vancouver, South Surrey, or anywhere else across our Metro Vancouver service area.",
  },
]

function BlogIVMyths() {
  useSeoMeta({
    title: "7 Common Myths About IV Nutrient Therapy | Mobile IV Therapy Vancouver | Haven Wellness Health",
    description:
      "Debunking the top 7 myths about IV nutrient therapy and mobile IV hydration. Learn the truth about safety, cost, and benefits of IV vitamin drips in Vancouver, Burnaby, Coquitlam, Surrey, Langley, Delta, White Rock, and North & West Vancouver.",
    ogTitle: "7 Common Myths About IV Nutrient Therapy | Haven Wellness Health",
    ogDescription:
      "The truth about mobile IV therapy, IV hydration, and vitamin infusions across Metro Vancouver, straight from a pharmacist-led clinical team.",
    ogImage: "https://static.lumi.new/76/768e65f062773d229ce3553277cb8250.webp",
    ogUrl: "https://www.havenintegrativehealthconsulting.com/blog/iv-therapy-myths",
    twitterCard: "summary_large_image",
    twitterImage: "https://static.lumi.new/76/768e65f062773d229ce3553277cb8250.webp",
  })

  useHead({
    link: [
      { rel: "canonical", href: "https://www.havenintegrativehealthconsulting.com/blog/iv-therapy-myths" },
    ],
    meta: [
      {
        name: "keywords",
        content:
          "IV nutrient therapy myths, mobile IV therapy, mobile IV hydration, IV vitamin therapy, IV drip myths, mobile IV therapy Vancouver, mobile IV therapy Burnaby, mobile IV therapy Coquitlam, mobile IV therapy Gilmore Place, mobile IV therapy Surrey, mobile IV therapy Langley, mobile IV therapy Delta, mobile IV therapy White Rock, mobile IV therapy South Surrey, mobile IV therapy North Vancouver, mobile IV therapy West Vancouver, IV hydration Lower Mainland, IV therapy near me, in-home IV therapy, IV nutrient therapy Vancouver, vitamin injections Vancouver, hangover IV therapy, mobile IV drip, registered nurse IV therapy, is IV therapy safe, does insurance cover IV therapy, IV therapy benefits, myths about IV therapy, mobile IV bar Vancouver, IV vitamin drip Lower Mainland",
      },
    ],
    script: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: "7 Common Myths About IV Nutrient Therapy",
          image: "https://static.lumi.new/76/768e65f062773d229ce3553277cb8250.webp",
          url: "https://www.havenintegrativehealthconsulting.com/blog/iv-therapy-myths",
          author: {
            "@type": "Organization",
            name: "Haven Wellness Health",
          },
          publisher: {
            "@type": "Organization",
            name: "Haven Integrative Health Consulting Inc.",
          },
          mainEntityOfPage: "https://www.havenintegrativehealthconsulting.com/blog/iv-therapy-myths",
          about: "Mobile IV Nutrient Therapy",
          articleSection: "IV Therapy Education",
          keywords: "mobile IV therapy, IV hydration, IV nutrient therapy myths, Metro Vancouver, Lower Mainland",
        }),
      },
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: myths.map((m) => ({
            "@type": "Question",
            name: m.myth,
            acceptedAnswer: {
              "@type": "Answer",
              text: m.truth,
            },
          })),
        }),
      },
    ],
  })

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <HavenNav />

      <main>
        {/* Hero */}
        <section className="pt-[110px] pb-16 lg:pt-[140px] lg:pb-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5] via-[#F5F0EB] to-[#FAF8F5]" />
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-12 relative">
            <Link
              to="/havenwellnesshealth"
              className="inline-flex items-center gap-2 text-[#9B8E7E] text-sm mb-8 hover:text-[#3D3A34] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Haven Wellness Health
            </Link>

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-[#E8E4DE] mb-6">
                  <Droplets className="w-4 h-4 text-[#9B8E7E]" />
                  <span className="text-xs uppercase tracking-[0.2em] text-[#9B8E7E] font-medium">IV Therapy Education</span>
                </div>
                <h1
                  className="text-3xl sm:text-5xl lg:text-6xl font-light mb-6 text-[#3D3A34] leading-[1.1]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  7 Common Myths About IV Nutrient Therapy
                </h1>
                <p className="text-lg sm:text-xl text-[#6B6761] mb-8 font-light leading-relaxed">
                  Think you know IV therapy? Let's clear up the misconceptions around mobile IV hydration, safety, and cost, with clear, clinician-reviewed answers from Haven Wellness Health, proudly serving Vancouver, Burnaby, Coquitlam, Surrey, Langley, Delta, White Rock, and every corner of the Lower Mainland.
                </p>
                <div className="flex items-center gap-5 text-sm text-[#9B8E7E] mb-8">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    7 min read
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    Serving Metro Vancouver
                  </span>
                </div>
                <a
                  href={JANE_BOOK_NOW}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#9B8E7E] text-white rounded-full font-medium hover:bg-[#8A7D6D] transition-all hover:shadow-lg"
                >
                  Book Mobile IV Therapy
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="relative"
              >
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://static.lumi.new/76/768e65f062773d229ce3553277cb8250.webp"
                    alt="Think you know IV therapy? Let's clear up 7 common myths about IV nutrient therapy"
                    className="w-full h-[420px] sm:h-[520px] lg:h-[600px] object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-10 sm:py-14 bg-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-12">
            <motion.p {...fadeUp} transition={{ duration: 0.7 }} className="text-lg text-[#6B6761] leading-relaxed">
              IV nutrient therapy has exploded in popularity across the Lower Mainland, but with popularity comes a wave of misinformation. As a pharmacist-led mobile IV therapy provider serving Vancouver, Burnaby, Coquitlam, Gilmore Place, New Westminster, Richmond, Surrey, Langley, Delta, White Rock, South Surrey, North Vancouver, and West Vancouver, our team hears the same misconceptions again and again. Below, we break down the seven most common myths about IV hydration and vitamin therapy, and what the evidence and clinical practice actually show.
            </motion.p>
          </div>
        </section>

        {/* Myths List */}
        <section className="py-14 sm:py-20 bg-[#FAF8F5]">
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-12 space-y-6">
            {myths.map((item, index) => (
              <motion.div
                key={item.number}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white border border-[#E8E4DE] rounded-3xl p-7 sm:p-10"
              >
                <div className="flex items-start gap-5 sm:gap-8">
                  <div
                    className="text-4xl sm:text-6xl font-light text-[#E8E4DE] flex-shrink-0 leading-none"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {item.number}
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0EDE8] mb-4">
                      <AlertCircle className="w-3.5 h-3.5 text-[#9B8E7E]" />
                      <span className="text-[11px] uppercase tracking-[0.15em] text-[#9B8E7E] font-medium">Myth</span>
                    </div>
                    <h2
                      className="text-xl sm:text-2xl lg:text-3xl font-light text-[#3D3A34] mb-4 leading-snug"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {item.myth}
                    </h2>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0EDE8] mb-4">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#9B8E7E]" />
                      <span className="text-[11px] uppercase tracking-[0.15em] text-[#9B8E7E] font-medium">The Truth</span>
                    </div>
                    <p className="text-[#6B6761] leading-relaxed">{item.truth}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-12">
            <motion.div {...fadeUp} transition={{ duration: 0.8 }} className="text-center mb-10">
              <p className="text-sm uppercase tracking-[0.25em] text-[#9B8E7E] mb-4 font-medium">Now You Know the Facts</p>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-light mb-6 text-[#3D3A34]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Mobile IV Therapy, Delivered Across the Lower Mainland
              </h2>
              <p className="text-[#6B6761] max-w-2xl mx-auto leading-relaxed">
                Haven Wellness Health brings pharmacist-approved, nurse-administered IV nutrient therapy directly to your home, office, or event throughout Metro Vancouver, including:
              </p>
            </motion.div>
            <div className="flex flex-wrap justify-center gap-3">
              {serviceAreas.map((area) => (
                <span
                  key={area}
                  className="px-4 py-2 rounded-full bg-[#FAF8F5] border border-[#E8E4DE] text-sm text-[#3D3A34]"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-14 sm:py-20 lg:py-28 bg-[#3D3A34] relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
          <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-12 text-center relative">
            <motion.div {...fadeUp} transition={{ duration: 0.8 }}>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 text-white leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Ready to experience mobile IV therapy for yourself?
              </h2>
              <p className="text-lg text-white/80 mb-10 font-light max-w-xl mx-auto">
                Book a pharmacist-approved, nurse-administered IV nutrient session at your home, office, or event anywhere across Vancouver and the Lower Mainland.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={JANE_BOOK_NOW}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-10 py-5 bg-white text-[#3D3A34] rounded-full font-medium text-lg hover:shadow-2xl transition-all hover:scale-105 inline-flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Book IV Therapy
                </a>
                <Link
                  to="/havenwellnesshealth#event-inquiry"
                  className="px-10 py-5 bg-transparent text-white rounded-full font-medium text-lg border border-white/30 hover:border-white/60 transition-all inline-flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  Inquire About Events
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#1F1D1A] text-white py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-10">
            <div className="col-span-2 md:col-span-1">
              <h4 className="text-2xl font-serif mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Haven</h4>
              <p className="text-white/60 text-sm leading-relaxed">Clinical expertise.<br />Elevated wellness.</p>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Our Divisions</h5>
              <ul className="space-y-3 text-sm text-white/70">
                <li><Link to="/havenwellnesshealth" className="hover:text-white transition-colors">Haven Wellness Health</Link></li>
                <li><Link to="/menopausehealth" className="hover:text-white transition-colors">Haven Menopause Health</Link></li>
                <li><Link to="/travelhealth" className="hover:text-white transition-colors">Haven Travel Health</Link></li>
                <li><Link to="/acnehealth" className="hover:text-white transition-colors">Haven Acne Health</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Company</h5>
              <ul className="space-y-3 text-sm text-white/70">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Our Team</Link></li>
                <li><Link to="/blog/iv-therapy-myths" className="hover:text-white transition-colors">IV Therapy Myths</Link></li>
                <li><Link to="/havenwellnesshealth#faq" className="hover:text-white transition-colors">FAQs</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">Legal</h5>
              <ul className="space-y-3 text-sm text-white/70">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-sm text-white/40">
            <p>&copy; 2024 Haven Integrative Health Consulting Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default BlogIVMyths
